import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPastEstimateCount, fetchSupersededCount, purgePastEstimates, purgeSupersededObservations } from "../../api/observationPurge";
import { ErrorState, LoadingState } from "../states";
import { useToast } from "../toast/toastContext";
import adminStyles from "./Admin.module.css";
import panel from "./AdminPanel.module.css";
import { Modal } from "./Modal";
import styles from "./ObservationCleanUpPanel.module.css";

type PurgeKind = "superseded" | "past";

/** A count that is loaded on the page, or requested by the administrator on demand. */
type CountState = { kind: "idle" } | { kind: "loading" } | { kind: "loaded"; rows: number } | { kind: "error"; message: string };

/** A count from the server, as a number of rows: a reply that carries none counts as nothing to remove. */
function rowCount(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function describeError(result: { kind: "denied" } | { kind: "conflict" | "error"; message: string }, deniedText: string): string {
  return result.kind === "denied" ? deniedText : result.message;
}

/**
 * One clean-up, as a card: an icon and its title, what it removes, then the number of rows it would remove as a figure, shown before
 * the button so the administrator sees the size of the change before confirming it. While the count loads, and if it fails, the card
 * keeps its place and says so inside itself.
 */
function PurgeCard({
  icon,
  title,
  description,
  count,
  busy,
  onCount,
  onPurge,
  onRetry,
  countLabel,
  purgeLabel,
  language,
  countingNote,
}: {
  icon: "history" | "estimate";
  title: string;
  description: string;
  count: CountState;
  busy: boolean;
  onCount?: () => void;
  onPurge: () => void;
  onRetry: () => void;
  countLabel?: string;
  purgeLabel: string;
  language: string;
  countingNote?: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <article className={styles.card}>
      <div className={styles.cardHead}>
        <span className={styles.icon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" focusable="false">
            {icon === "history" ? (
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2" />
            ) : (
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM9 15l2 2 4-4" />
            )}
          </svg>
        </span>
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      <p className={styles.description}>{description}</p>

      <div className={styles.figure} data-state={count.kind === "loaded" ? (count.rows > 0 ? "rows" : "none") : count.kind}>
        {count.kind === "loaded" && (
          <>
            <span className={styles.figureValue} aria-hidden="true">
              {count.rows.toLocaleString(language)}
            </span>
            <span className={styles.figureLabel}>
              {count.rows > 0 ? t("admin.syncStatus.cleanUp.wouldRemove", { rows: count.rows.toLocaleString(language) }) : t("admin.syncStatus.cleanUp.nothingToRemove")}
            </span>
          </>
        )}
        {count.kind === "loading" && <LoadingState label={countingNote ?? t("admin.syncStatus.loading")} />}
        {count.kind === "idle" && <span className={styles.figureLabel}>{t("admin.syncStatus.cleanUp.notCounted")}</span>}
        {count.kind === "error" && <ErrorState title={t("admin.syncStatus.cleanUp.countFailed")} description={count.message} onRetry={onRetry} />}
      </div>

      <div className={styles.actions}>
        {onCount !== undefined && count.kind !== "loaded" && count.kind !== "error" && (
          <button type="button" className="button button-secondary" onClick={onCount} disabled={count.kind === "loading" || busy}>
            {countLabel}
          </button>
        )}
        {count.kind === "loaded" && (
          <button type="button" className="button button-primary" onClick={onPurge} disabled={count.rows === 0 || busy}>
            {purgeLabel}
          </button>
        )}
      </div>
    </article>
  );
}

/**
 * The clean-up section of the Sync status screen. Superseded figures (older copies that a newer fetch replaced) are
 * counted on demand, because counting them scans the whole history. Past-day estimates (provider figures the portal
 * estimated for a day that has since ended) are counted on load. Each action asks first, and reports its outcome as a toast.
 */
export function ObservationCleanUpPanel(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [past, setPast] = useState<CountState>({ kind: "loading" });
  const [superseded, setSuperseded] = useState<CountState>({ kind: "idle" });
  const [confirming, setConfirming] = useState<PurgeKind | null>(null);
  const [busy, setBusy] = useState<PurgeKind | null>(null);

  const deniedText = t("admin.syncStatus.denied.description");

  const loadPast = useCallback(async () => {
    setPast({ kind: "loading" });
    const result = await fetchPastEstimateCount();
    setPast(result.kind === "ok" ? { kind: "loaded", rows: rowCount(result.response.pastEstimateRows) } : { kind: "error", message: describeError(result, deniedText) });
  }, [deniedText]);

  const countSuperseded = useCallback(async () => {
    setSuperseded({ kind: "loading" });
    const result = await fetchSupersededCount();
    setSuperseded(result.kind === "ok" ? { kind: "loaded", rows: rowCount(result.response.supersededRows) } : { kind: "error", message: describeError(result, deniedText) });
  }, [deniedText]);

  useEffect(() => {
    void loadPast();
  }, [loadPast]);

  const runPurge = async (kind: PurgeKind): Promise<void> => {
    setConfirming(null);
    setBusy(kind);
    const result = kind === "superseded" ? await purgeSupersededObservations() : await purgePastEstimates();
    setBusy(null);
    if (result.kind === "ok") {
      const rows = result.response.rowsDeleted.toLocaleString(i18n.language);
      toast.show(t(kind === "superseded" ? "admin.syncStatus.cleanUp.purgedSuperseded" : "admin.syncStatus.cleanUp.purgedPast", { rows }), "success");
      // The superseded count is now stale; the past count is recounted.
      setSuperseded({ kind: "idle" });
      void loadPast();
    } else {
      toast.show(describeError(result, deniedText), "error");
    }
  };

  return (
    <section className={panel.panel} aria-labelledby="observation-clean-up-heading">
      <div className={panel.header}>
        <h2 id="observation-clean-up-heading" className={`panel-title ${panel.title}`}>
          {t("admin.syncStatus.cleanUp.title")}
        </h2>
      </div>
      <p className={panel.intro}>{t("admin.syncStatus.cleanUp.intro")}</p>

      <div className={styles.cards}>
        <PurgeCard
          icon="history"
          title={t("admin.syncStatus.cleanUp.supersededTitle")}
          description={t("admin.syncStatus.cleanUp.supersededDescription")}
          count={superseded}
          busy={busy !== null}
          onCount={() => void countSuperseded()}
          onPurge={() => setConfirming("superseded")}
          onRetry={() => void countSuperseded()}
          countLabel={t("admin.syncStatus.cleanUp.countSuperseded")}
          purgeLabel={t("admin.syncStatus.cleanUp.purgeSuperseded")}
          language={i18n.language}
          countingNote={t("admin.syncStatus.cleanUp.countingSuperseded")}
        />
        <PurgeCard
          icon="estimate"
          title={t("admin.syncStatus.cleanUp.pastTitle")}
          description={t("admin.syncStatus.cleanUp.pastDescription")}
          count={past}
          busy={busy !== null}
          onPurge={() => setConfirming("past")}
          onRetry={() => void loadPast()}
          purgeLabel={t("admin.syncStatus.cleanUp.purgePast")}
          language={i18n.language}
        />
      </div>

      {confirming === "superseded" && superseded.kind === "loaded" && (
        <Modal title={t("admin.syncStatus.cleanUp.supersededConfirmTitle")} onClose={() => setConfirming(null)}>
          <p>{t("admin.syncStatus.cleanUp.supersededConfirmBody", { rows: superseded.rows.toLocaleString(i18n.language) })}</p>
          <div className={adminStyles.syncActions}>
            <button type="button" className="button button-secondary" onClick={() => setConfirming(null)}>
              {t("admin.syncStatus.cancel")}
            </button>
            <button type="button" className="button button-primary" data-autofocus onClick={() => void runPurge("superseded")}>
              {t("admin.syncStatus.cleanUp.purgeSuperseded")}
            </button>
          </div>
        </Modal>
      )}

      {confirming === "past" && past.kind === "loaded" && (
        <Modal title={t("admin.syncStatus.cleanUp.pastConfirmTitle")} onClose={() => setConfirming(null)}>
          <p>{t("admin.syncStatus.cleanUp.pastConfirmBody", { rows: past.rows.toLocaleString(i18n.language) })}</p>
          <div className={adminStyles.syncActions}>
            <button type="button" className="button button-secondary" onClick={() => setConfirming(null)}>
              {t("admin.syncStatus.cancel")}
            </button>
            <button type="button" className="button button-primary" data-autofocus onClick={() => void runPurge("past")}>
              {t("admin.syncStatus.cleanUp.purgePast")}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
